import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X, Loader2 } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../utils/cn';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** Static option list. Ignored when `loadOptions` is provided. */
  options?: SearchableSelectOption[];
  /**
   * Async loader. Called once on first open; options are cached thereafter.
   * Any subsequent error surfaces inline.
   */
  loadOptions?: () => Promise<SearchableSelectOption[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  clearable?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  required?: boolean;
  /**
   * When true, user can press Enter on the search query to commit it as the
   * value (even if it doesn't match any loaded option). Useful for fields
   * like Brand where users may type a new value that doesn't yet exist.
   */
  allowCustom?: boolean;
  /**
   * Panel placement — `bottom` (open downward), `top` (open upward), or
   * `auto` (default) which measures the trigger's viewport rect at
   * open-time and flips upward when there isn't enough room below.
   * Fixes the SSO Purchase-Order grid bug where the row-level picker
   * overlapped content beneath the table.
   */
  placement?: 'top' | 'bottom' | 'auto';
  /**
   * External loading flag — set to `true` while the caller is still
   * populating a static `options` list. When set, the trigger renders
   * "Loading…" (instead of the placeholder) for a `value` that hasn't
   * yet resolved to an option, so an edit page doesn't briefly flash a
   * blank picker while the product cache warms up. Ignored when the
   * component owns its own async `loadOptions` — it already tracks
   * its internal loading state in that path.
   */
  loading?: boolean;
}

export function SearchableSelect({
  label,
  value,
  onChange,
  options: staticOptions,
  loadOptions,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyLabel = 'No matches',
  clearable = true,
  disabled,
  error,
  className,
  required,
  allowCustom,
  placement = 'auto',
  loading: externalLoading,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loadedOptions, setLoadedOptions] = useState<SearchableSelectOption[] | null>(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * Resolved panel direction — derived from `placement` on every open.
   * `auto` measures once at open-time; explicit `top`/`bottom` skip the
   * measurement. Kept in state so the panel can render with the right
   * `top-full` / `bottom-full` class on the same flush as `open`.
   */
  const [actualPlacement, setActualPlacement] = useState<'top' | 'bottom'>('bottom');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  /**
   * Loading indicator surfaced to the trigger label. The internal
   * `loadOptions` path drives its own state; static-options callers can
   * flip on the external `loading` prop while their upstream list is
   * warming up so the picker shows "Loading…" instead of a blank
   * placeholder for the persisted `value`.
   */
  const loading = internalLoading || externalLoading === true;

  const options = staticOptions ?? loadedOptions ?? [];

  // Eager-load the async options list on mount (verified 2026-05-05
  // bug-bash: users were clicking dropdowns and seeing an empty list
  // because the load was deferred until first open. Loading on
  // mount means options are already in state when the user clicks
  // — and any load error is visible immediately).
  useEffect(() => {
    if (!loadOptions) return;
    let cancelled = false;
    setInternalLoading(true);
    setLoadError(null);
    loadOptions()
      .then((opts) => {
        if (cancelled) return;
        setLoadedOptions(opts);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to load options';
        setLoadError(msg);
      })
      .finally(() => {
        if (!cancelled) setInternalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadOptions]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const commitCustom = () => {
    const q = query.trim();
    if (!q) return;
    if (options.some((o) => o.value.toLowerCase() === q.toLowerCase())) return;
    pick(q);
  };

  const canCommitCustom =
    !!allowCustom &&
    !!query.trim() &&
    !options.some(
      (o) =>
        o.value.toLowerCase() === query.trim().toLowerCase() ||
        o.label.toLowerCase() === query.trim().toLowerCase()
    );

  /**
   * Approx panel height used by the `auto` placement heuristic — the
   * search input (~40px) + the max-h-60 result list (240px) + a small
   * padding buffer. If the trigger's rect leaves less room than this
   * below AND more room above, flip the panel upward. Otherwise stay
   * downward (the historical default).
   */
  const APPROX_PANEL_HEIGHT = 300;
  const handleTriggerClick = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    if (placement === 'auto') {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setActualPlacement(
          spaceBelow < APPROX_PANEL_HEIGHT && spaceAbove > spaceBelow ? 'top' : 'bottom',
        );
      } else {
        setActualPlacement('bottom');
      }
    } else {
      setActualPlacement(placement);
    }
    setOpen(true);
  };

  return (
    <div className={cn('flex w-full flex-col gap-1', className)} ref={ref}>
      {label && (
        <span className="text-xs font-medium text-content-secondary">
          {label} {required && <span className="text-danger">*</span>}
        </span>
      )}
      {/* Single relative wrapper — trigger + panel share the container so
          the panel can position itself with `top-full` (open downward) or
          `bottom-full` (open upward) depending on the resolved placement.
          Previously the panel was in a sibling div and always used
          `top-1`, so it could only open downward and overlapped whatever
          sat below the trigger (SSO Purchase-Order grid regression). */}
      <div className="relative">
        <div
          ref={triggerRef}
          onClick={handleTriggerClick}
          className={cn(
            'flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border px-3 transition-colors',
            open ? 'border-primary' : 'border-border',
            error && 'border-danger',
            disabled && 'cursor-not-allowed opacity-50',
            'bg-surface-card'
          )}
        >
          <span
            className={cn(
              'flex-1 truncate text-sm',
              selected || (allowCustom && value) || (value && loading)
                ? 'text-content-primary'
                : 'text-content-muted'
            )}
          >
            {selected
              ? selected.label
              : allowCustom && value
                ? value
                : value && loading
                  ? 'Loading…'
                  : placeholder}
          </span>
          {clearable && (selected || (allowCustom && value)) && !disabled && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear"
              className="flex h-5 w-5 items-center justify-center rounded-full text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-content-muted transition-transform',
              open && 'rotate-180'
            )}
          />
        </div>

        {open && (
          <div
            className={cn(
              'absolute left-0 right-0 z-50 rounded-md border border-border bg-surface-card shadow-xl',
              actualPlacement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-3.5 w-3.5 text-content-muted" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    setQuery('');
                  } else if (e.key === 'Enter' && canCommitCustom) {
                    e.preventDefault();
                    commitCustom();
                  }
                }}
                className="w-full bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
              />
              {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-content-muted" />}
            </div>

            <div className="max-h-60 overflow-y-auto py-1">
              {loadError && (
                <div className="px-3 py-2 text-xs text-danger">{loadError}</div>
              )}
              {canCommitCustom && (
                <button
                  type="button"
                  onClick={commitCustom}
                  className="flex w-full items-center gap-2 border-b border-border/60 px-3 py-2 text-left text-sm text-primary hover:bg-primary-light"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide">Use</span>
                  <span className="flex-1 truncate font-medium">"{query.trim()}"</span>
                  <span className="text-[10px] text-content-muted">Enter</span>
                </button>
              )}
              {!loading && !loadError && filtered.length === 0 && !canCommitCustom && (
                <div className="px-3 py-2 text-xs text-content-muted">
                  {emptyLabel}
                </div>
              )}
              {filtered.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => pick(opt.value)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
                      isSelected
                        ? 'bg-primary-light text-primary font-medium'
                        : 'text-content-secondary hover:bg-surface-card-hover'
                    )}
                  >
                    <span className="w-full truncate">{opt.label}</span>
                    {opt.description && (
                      <span className="w-full truncate text-[11px] text-content-muted">
                        {opt.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}
